import React from 'react'
import DoubleStatsTable from 'components/generic/DoubleStatsTable'
import { VsInfo } from 'types/global'
import { getAgeReference } from 'utils'
import { SexEnum } from 'types/api/enums'
import { useTranslation } from 'react-i18next'
import { getWinLikelihoodDisplayText } from './utils'
import LogInToView from './log-in-to-view/LogInToView'
import styles from './styles/h2h.module.scss'
import { useCountries } from 'hooks/use-countries'
import { Flag } from 'components/generic/Country'
import useWindowSize from 'hooks/use-windowSize'
import { TABLET_BREAKPOINT } from 'constants/global'
import cx from 'classnames'
import LowConfidenceText from './low-confidence-text/LowConfidenceText'
import RatingWithConfidenceTick from 'components/generic/RatingWithConfidenceTick'

const styleRefs = {
  1: 'player1',
  2: 'player2',
}
interface Props extends VsInfo {
  isLoggedIn: boolean
}

const H2HStats = ({ players, wins, winLikelihood, isLoggedIn }: Props) => {
  const { t } = useTranslation(['strH2H', 'strCommonUI'])
  const { getCountryName } = useCountries()
  const windowWidth = useWindowSize().width
  const isMobile = windowWidth && windowWidth < TABLET_BREAKPOINT
  const [p1, p2] = players
  const [winLHPlayer1, winLHPlayer2] = winLikelihood
  const p1Info = {
    ...p1,
    playerRef: 1,
    wins: wins.p1,
    winLikelihoodSingles: winLHPlayer1.singles,
    winLikelihoodDoubles: winLHPlayer1.doubles,
  }
  const p2Info = {
    ...p2,
    playerRef: 2,
    wins: wins.p2,
    winLikelihoodSingles: winLHPlayer2.singles,
    winLikelihoodDoubles: winLHPlayer2.doubles,
  }

  return (
    <DoubleStatsTable
      side1={p1Info}
      side2={p2Info}
      stats={[
        ...(isMobile
          ? [
              {
                title: t('totalWins', { ns: 'strH2H' }),
                value: (p: typeof p1Info) => (
                  <span className={cx(styles.totalWins, styleRefs[p.playerRef])}>
                    {t('winsWithCount', { ns: 'strH2H', count: p.wins })}
                  </span>
                ),
              },
            ]
          : []),
        {
          title: t('wtnSingles', { ns: 'strH2H' }),
          value: p =>
            p.wtnSingles ? (
              <RatingWithConfidenceTick
                rating={p.wtnSingles}
                confidence={p.wtnConfidenceSingles}
                classNames={{ confidence: styles.confidenceRibbon }}
              />
            ) : null,
        },
        {
          title: t('wtnDoubles', { ns: 'strH2H' }),
          value: p =>
            p.wtnDoubles ? (
              <RatingWithConfidenceTick
                rating={p.wtnDoubles}
                confidence={p.wtnConfidenceDoubles}
                classNames={{ confidence: styles.confidenceRibbon }}
              />
            ) : null,
        },
        {
          title: t('winLikelihood.singlesLabel', { ns: 'strH2H' }),
          value: p => (
            <WinLikelihoodCell isLoggedIn={isLoggedIn} winLikelihood={p.winLikelihoodSingles} />
          ),
        },
        {
          title: t('gender', { ns: 'strH2H' }),
          value: p =>
            p.sex === SexEnum.M
              ? t('form.sexMale', { ns: 'strCommonUI' })
              : t('form.sexFemale', { ns: 'strCommonUI' }),
        },
        {
          title: t('age', { ns: 'strH2H' }),
          value: ({ age, birthYear }) => getAgeReference(age, birthYear),
        },
        {
          title: t('nationality', { ns: 'strH2H' }),
          value: p => (
            <p className={styles.countryContainer}>
              <Flag alpha3={p.countryCode} /> <span>{getCountryName(p.countryCode)}</span>
            </p>
          ),
        },
      ]}
    />
  )
}

const WinLikelihoodCell = ({
  isLoggedIn,
  winLikelihood,
}: {
  winLikelihood?: number
  isLoggedIn: boolean
}) => {
  const { t } = useTranslation('strH2H')
  if (!isLoggedIn)
    return (
      <div className={styles.loginTextContainer}>
        <LogInToView />
      </div>
    )

  if (typeof winLikelihood !== 'number') return <>{t('winLikelihood.unavailable')}</>

  if (winLikelihood < 0) return <LowConfidenceText />

  return <>{getWinLikelihoodDisplayText(winLikelihood, t)}</>
}

export default H2HStats
